import { useRouter } from 'next/router';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { InviteCodeState } from './types';

export const inviteCodeQueryString = 'invite_code';
export const InviteCodeContext = createContext<InviteCodeState>(undefined!);

export function InviteCodeProvider({
    children,
}: {
    children: ReactNode | ReactNode[];
}) {
    const { query, replace, pathname, isReady } = useRouter();
    const { [inviteCodeQueryString]: queryCode } = query;

    const [stateCode, setStateCode] = useState<string>('');

    const inviteCode = useMemo(
        () => (stateCode ? stateCode : queryCode ? (queryCode as string) : ''),
        [queryCode, stateCode]
    );

    const setInviteCode = useCallback(
        (code: string, willSetQuery = false) => {
            setStateCode(code);
            if (willSetQuery) {
                query[inviteCodeQueryString] = code;
                replace({ pathname, query: query }, undefined, {
                    shallow: true,
                });
            }
        },
        [pathname, query, replace]
    );

    const removeInviteCode = useCallback(() => {
        delete query[inviteCodeQueryString];
        replace({ pathname, query: query }, undefined, {
            shallow: true,
        });
        setStateCode('');
    }, [pathname, query, replace]);

    return (
        <InviteCodeContext.Provider
            value={{
                isReady,
                inviteCode,
                hasInviteCode: !!inviteCode,
                isInviteCodeFromQuery: !!inviteCode && queryCode === inviteCode,
                setInviteCode,
                removeInviteCode,
            }}
        >
            {children}
        </InviteCodeContext.Provider>
    );
}
